import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Building2, CheckCircle2, XCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { PasswordInput } from "@/components/PasswordInput";
import { PasswordStrength, isPasswordStrong } from "@/components/PasswordStrength";

const Shell = ({ children }) => <div className="container-x py-20 max-w-md" data-testid="invite-page">{children}</div>;

const Closed = ({ status }) => (
  <Shell>
    <div className="card-soft text-center" data-testid="invite-closed">
      <XCircle className="w-10 h-10 mx-auto text-[#8A9A86]"/>
      <h1 className="font-heading text-2xl text-[#1E3F33] mt-4">{status === "accepted" ? "Invite already accepted" : "This invite is no longer active"}</h1>
      <p className="text-sm text-[#5C6B64] mt-2">{status === "accepted" ? "Sign in to manage your profile and leads." : "Ask your company to resend the invite."}</p>
      <Link to="/login" className="btn-primary inline-block mt-6" data-testid="invite-closed-login">Sign in</Link>
    </div>
  </Shell>
);

const NewCollectorForm = ({ token, inv }) => {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: "", password: "", email: inv.email, phone: inv.phone });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!isPasswordStrong(form.password)) return toast.error("Choose a stronger password: 8+ characters with upper & lower case letters and a number.");
    setBusy(true);
    try {
      const { data } = await api.post(`/invites/${token}/register`, { ...form, email: form.email || null, phone: form.phone || null });
      setUser(data);
      toast.success("Your login is ready");
      navigate("/dashboard/collector");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4 mt-8" data-testid="invite-register-form">
      <div><label className="label-soft">Username</label><input required minLength={3} maxLength={30} data-testid="invite-username-input" className="input-soft" value={form.username} onChange={set("username")} autoCapitalize="none" autoComplete="username"/></div>
      <div><label className="label-soft">Email</label><input type="email" data-testid="invite-email-input" className="input-soft" value={form.email} onChange={set("email")}/></div>
      <div><label className="label-soft">Phone</label><input type="tel" data-testid="invite-phone-input" className="input-soft" value={form.phone} onChange={set("phone")}/></div>
      <div>
        <label className="label-soft">Password</label>
        <PasswordInput testId="invite-password-input" toggleTestId="invite-password-toggle" required minLength={8} value={form.password} onChange={set("password")} autoComplete="new-password" />
        <PasswordStrength password={form.password} testId="invite-password-strength" />
      </div>
      <p className="text-xs text-[#8A9A86]">{inv.company_name} accepted the platform's <a href="/legal/company-agreement" target="_blank" rel="noreferrer" className="text-[#CC5A3A] hover:underline">Company Account Agreement</a> on your behalf and covers your subscription.</p>
      <button disabled={busy} data-testid="invite-register-submit" className="btn-primary w-full">{busy ? "Creating login…" : "Create my login"}</button>
    </form>
  );
};

const JoinPanel = ({ token, inv }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const accept = async () => {
    setBusy(true);
    try { await api.post(`/invites/${token}/accept`); toast.success(`You joined ${inv.company_name}`); navigate("/dashboard/collector"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  if (!user || user.role !== "collector") {
    return (
      <div className="mt-8" data-testid="invite-login-required">
        <p className="text-sm text-[#5C6B64]">Sign in to your collector account to review and accept this invite.</p>
        <Link to="/login" state={{ from: `/invite/${token}` }} className="btn-primary inline-block mt-4" data-testid="invite-login-link">Sign in to accept</Link>
      </div>
    );
  }
  return (
    <div className="mt-8 space-y-4" data-testid="invite-join-panel">
      <ul className="text-sm text-[#5C6B64] space-y-2 list-disc pl-5">
        <li>{inv.company_name} will cover your $10.99/month subscription from now on.</li>
        <li>Your own subscription (if any) stops renewing — any days you've already paid for still count.</li>
        <li>Your company can view and respond to leads you receive after joining. You can leave anytime.</li>
      </ul>
      <button onClick={accept} disabled={busy} data-testid="invite-accept-button" className="btn-primary w-full inline-flex items-center justify-center gap-2"><CheckCircle2 className="w-4 h-4"/> {busy ? "Joining…" : `Join ${inv.company_name}`}</button>
    </div>
  );
};

export default function InviteAccept() {
  const { user } = useAuth();
  const { token } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/invites/${token}`).then(({ data }) => setInv(data)).catch(() => setError(true));
  }, [token]);

  const decline = async () => {
    if (!window.confirm(`Decline the invite from ${inv.company_name}?`)) return;
    try { await api.post(`/invites/${token}/decline`); toast.success("Invite declined"); navigate("/"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  if (error) return <Closed status="invalid" />;
  if (!inv) return <Shell><p className="text-[#5C6B64]">Loading…</p></Shell>;
  if (inv.status !== "pending") return <Closed status={inv.status} />;

  return (
    <Shell>
      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#CC5A3A] mb-3 inline-flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5"/> Company invite</div>
      <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold" data-testid="invite-title">
        {inv.kind === "new" ? `Hi ${inv.name}, set up your login` : `${inv.company_name} invited you to join`}
      </h1>
      <p className="text-[#5C6B64] mt-2">
        {inv.kind === "new"
          ? `${inv.company_name} listed you as a collector on Screening Services Mobile. Create your login to manage your profile, certifications and customer leads.`
          : "Join their company account so they can manage your subscription and help respond to your leads."}
      </p>
      {inv.kind === "new" ? <NewCollectorForm token={token} inv={inv} /> : <JoinPanel token={token} inv={inv} />}
      {(inv.kind === "new" || user?.role === "collector") && <button type="button" onClick={decline} data-testid="invite-decline-button" className="text-sm text-[#8A9A86] hover:text-[#E63946] mt-6">Not you, or don't want to join? Decline invite</button>}
    </Shell>
  );
}
